import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  NoticiaEditor,
  type NoticiaEditorSubmit,
} from '@/components/noticias/noticia-editor'
import {
  createNoticia,
  uploadNoticiaImagen,
  type CreateNoticiaBody,
} from '@/features/noticias/api'
import type { NoticiaFormValues } from '@/features/noticias/schema'

export function NoticiaNewPage(): ReactNode {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)

  const initialValues = useMemo<NoticiaFormValues>(() => {
    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    return {
      titulo: '',
      descripcion: '',
      ctaTexto: '',
      ctaUrl: '',
      fechaPublicacion: `${yyyy}-${mm}-${dd}`,
      featured: false,
      estado: 'BORRADOR',
    }
  }, [])

  const createMutation = useMutation({
    mutationFn: async ({ values, imageFile }: NoticiaEditorSubmit) => {
      const body: CreateNoticiaBody = {
        titulo: values.titulo.trim(),
        descripcion: values.descripcion.trim(),
        ctaTexto: values.ctaTexto.trim(),
        ctaUrl: values.ctaUrl.trim(),
        fechaPublicacion: values.fechaPublicacion,
        featured: values.featured,
        estado: values.estado,
      }
      const created = await createNoticia(body)
      if (imageFile) {
        // Si la subida de imagen falla, la noticia queda creada igual. Se lo
        // decimos al admin con un toast separado para que pueda reintentar
        // desde la pantalla de edicion.
        try {
          await uploadNoticiaImagen(created.id, imageFile)
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'No pudimos subir la imagen.'
          toast.error(`Noticia creada, pero falló la imagen: ${msg}`)
          return created
        }
      }
      return created
    },
    onSuccess: () => {
      toast.success('Noticia creada')
      void queryClient.invalidateQueries({ queryKey: ['noticias'] })
      navigate('/dashboard/noticias')
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'No pudimos crear la noticia.'
      toast.error(msg)
    },
  })

  async function onSubmit(payload: NoticiaEditorSubmit) {
    setSubmitting(true)
    try {
      await createMutation.mutateAsync(payload)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <NoticiaEditor
      title="Nueva noticia"
      description="Publica una noticia en el feed de la app."
      initialValues={initialValues}
      submitLabel="Guardar"
      submitting={submitting}
      onSubmit={onSubmit}
      onCancel={() => navigate('/dashboard/noticias')}
    />
  )
}
